import { z } from 'zod';

/**
 * Rapportage zoals het over de lijn komt.
 *
 * Elk schema volgt één API Resource uit `smartkeur-api`
 * (`app/Http/Api/V1/Resources/Reporting`, tussen haakjes) en de bijbehorende
 * `meta` van de controllers. De schermen zien deze vorm nooit: `model.ts` maakt
 * er het model van. Identiteit is een `uuid`, een verwijzing `<relatie>_uuid`;
 * een bevinding heeft een `key` (`02-api-contract.md` §Rapportage).
 */

/** ISO-8601 met tijdzone. */
const timestamp = z.string();

/** Bedrag als string met twee decimalen: `"1450.00"`. */
const amount = z.string();

/** De drie kostenblokken, in de sleutels van de app. */
export const reportCostsResourceSchema = z.object({
    direct: amount,
    termijn: amount,
    onderhoud: amount,
});

export type ReportCostsResource = z.infer<typeof reportCostsResourceSchema>;

export const reportSectionResourceSchema = z.object({
    uuid: z.string(),
    key: z.string(),
    /** `summary` · `guide` · `costs` · `part` · `nhg` · `conditions` */
    kind: z.string(),
    label: z.string(),
    pages: z.string(),
    /** Het boompad van het onderdeel in de opname (`13.2`); `null` buiten een `part`. */
    section_path: z.string().nullable().default(null),
    body: z.string().nullable(),
    body_is_generated: z.boolean(),
    body_updated_at: timestamp.nullable(),
    /** In de bijlage "Toelichting en voorwaarden", zoals in de PDF; een ouder antwoord heeft het niet. */
    appendix: z.boolean().optional(),
});

export type ReportSectionResource = z.infer<typeof reportSectionResourceSchema>;

/** Een bedrag dat met de hand is gezet, of `null` waar de berekening telt. */
const ownAmount = amount.nullable();

/** Waar een bevinding in de NHG-bijlage landt. */
export const reportFindingNhgResourceSchema = z.object({
    /** De vaste regel (`A.3`, `B.1.2` …), of `null` buiten de tabellen. */
    row: z.string().nullable(),
    element: z.string().nullable(),
    direct: amount,
    termijn: amount,
    onderhoud: amount,
    /** `no_code` · `excluded` · `null` */
    outside_reason: z.string().nullable(),
});

/** Wie de bedragen of de NHG-keuze na het indienen aanpaste, en of de inspecteur akkoord is. */
export const reportFindingAdjustmentResourceSchema = z.object({
    by_name: z.string().nullable(),
    at: timestamp.nullable(),
    reason: z.string().nullable(),
    by_inspector: z.boolean(),
    awaits_inspector: z.boolean(),
    confirmed_by_name: z.string().nullable(),
    confirmed_at: timestamp.nullable(),
});

export const reportFindingResourceSchema = z.object({
    key: z.string(),
    part: z.string(),
    section: z.string(),
    finding: z.string(),
    explanation: z.string(),
    /** Waar het gebrek zit, in de woorden van de inspecteur. */
    location: z.string().nullable().default(null),
    urgency: z.string(),
    /** `direct` · `termijn` · `onderhoud` */
    urgency_key: z.string().nullable(),
    /** `good` · `attention` · `defect` · `none` */
    severity: z.string(),
    cost: amount,
    costs: reportCostsResourceSchema,
    nhg_code: z.string().nullable(),
    /** Het nummer van de kop waaronder de bevinding staat: de locatie in de NHG-bijlage. */
    section_number: z.string().default(''),
    /**
     * Het boompad van de knoop in de opname waar deze bevinding vandaan komt
     * (`13.2.1`), dezelfde nummering die de engine geeft. Leeg bij een handmatige
     * bevinding zonder plek in de boom.
     */
    section_path: z.string().default(''),
    /** `answer` · `manual` */
    source: z.string().default('answer'),
    /** `direct` · `vijfjaar` · `niet` · `null` */
    nhg_classification: z.string().nullable().default(null),
    own_nhg_code: z.string().nullable().default(null),
    nhg: reportFindingNhgResourceSchema.nullable().default(null),
    /** Het ingevulde aantal bij een vraag naar een hoeveelheid; `null` bij een vast bedrag. */
    quantity: z.number().nullable().default(null),
    unit_costs: reportCostsResourceSchema.nullable().default(null),
    calculated_costs: reportCostsResourceSchema.nullable().default(null),
    costs_adjusted: z.boolean().default(false),
    own_amounts: z
        .object({ direct: ownAmount, termijn: ownAmount, onderhoud: ownAmount })
        .nullable()
        .default(null),
    adjustment: reportFindingAdjustmentResourceSchema.nullable().default(null),
});

const nhgNecessaryResourceSchema = z.object({ direct: amount, termijn: amount });

/** De NHG-bijlage zoals in de PDF (`Reporting\NhgAppendixResource::forOffice`). */
export const reportNhgResourceSchema = z.object({
    enabled: z.boolean(),
    has_findings: z.boolean(),
    tables: z.array(
        z.object({
            /** `general` · `storeys` */
            key: z.string(),
            label: z.string(),
            rows: z.array(
                z.object({
                    code: z.string(),
                    element: z.string(),
                    locations: z.array(z.string()),
                    action: z.string(),
                    direct: amount,
                    termijn: amount,
                    finding_keys: z.array(z.string()),
                }),
            ),
            total: nhgNecessaryResourceSchema,
        }),
    ),
    total: nhgNecessaryResourceSchema,
    improvement: amount,
    outside: z.object({ direct: amount, termijn: amount, onderhoud: amount, count: z.number() }),
});

export type ReportNhgResource = z.infer<typeof reportNhgResourceSchema>;

export type ReportFindingResource = z.infer<typeof reportFindingResourceSchema>;

export const reportPhotoResourceSchema = z.object({
    uuid: z.string(),
    caption: z.string(),
    url: z.string(),
});

export const reportRecipientResourceSchema = z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    role: z.string(),
    /** `client` · `agent` · `advisor` */
    role_key: z.string(),
    sent_at: timestamp.nullable(),
});

/** (`Reporting\ReportResource`) - `GET /reports/{report}` en `GET /orders/{order}/report`. */
export const reportResourceSchema = z.object({
    uuid: z.string(),
    order_uuid: z.string(),
    order_number: z.string(),
    version: z.number(),
    /** `concept` · `goedgekeurd` · `verstuurd` */
    status: z.string(),
    /** `generating` · `draft` · `approved` · `sent` · `failed` · `superseded` */
    state: z.string(),
    state_label: z.string(),
    editable: z.boolean(),
    /** De opname van de keuring staat (weer) open: goedkeuren wordt dan geweigerd. */
    survey_open: z.boolean(),
    failure_reason: z.string().nullable(),
    norm_label: z.string(),
    kind_label: z.string(),
    service: z.string(),
    address: z.object({
        street: z.string(),
        house_number: z.string(),
        postal_code: z.string(),
        city: z.string(),
    }),
    requested_at: timestamp.nullable(),
    generated_at: timestamp.nullable(),
    generation_duration_ms: z.number().nullable(),
    minutes_after_inspection: z.number(),
    client_name: z.string(),
    inspected_on: z.string(),
    inspector_name: z.string(),
    costs: reportCostsResourceSchema,
    cost_groups: z.array(reportCostsResourceSchema.extend({ name: z.string() })),
    counts: z.object({
        findings: z.number(),
        defects: z.number(),
        attention_points: z.number(),
        photos: z.number(),
        parts: z.number(),
        parts_without_findings: z.number(),
        questions_visible: z.number(),
        questions_answered: z.number(),
    }),
    summary: z.string(),
    sections: z.array(reportSectionResourceSchema),
    findings: z.array(reportFindingResourceSchema),
    nhg: reportNhgResourceSchema.nullable().default(null),
    /** Aangepaste bedragen waar de inspecteur nog niet mee akkoord is; goedkeuren wacht erop. */
    adjustments_awaiting_inspector: z.number().default(0),
    photos: z.array(reportPhotoResourceSchema),
    /** Foto's van de opname zonder miniatuur en webversie: in geen rapport te zien. */
    photos_unprocessed: z
        .object({ failed: z.number(), waiting: z.number() })
        .default({ failed: 0, waiting: 0 }),
    recipients: z.array(reportRecipientResourceSchema),
    approved_at: timestamp.nullable(),
    approved_by_name: z.string().nullable(),
    sent_at: timestamp.nullable(),
    /* Ontbreekt bij een server van vóór dit veld; dan is het niet bekend. */
    replaces_version: z.number().int().nullable().optional(),
    pdf_url: z.string(),
    page_count: z.number().nullable(),
    /** De talen waarin de klant het rapport kan krijgen, Nederlands eerst; `["nl"]` als vertalen uit staat. */
    client_languages: z.array(z.string()).default(['nl']),
    can: z.object({
        edit: z.boolean(),
        approve: z.boolean(),
        send: z.boolean(),
        share: z.boolean(),
        /** Bedragen, NHG-keuze en NHG-onderdeel van een bevinding aanpassen (in een concept). */
        adjust: z.boolean().default(false),
        /** Akkoord geven op zo'n aanpassing: alleen de inspecteur van de opname. */
        confirm: z.boolean().default(false),
    }),
});

export type ReportResource = z.infer<typeof reportResourceSchema>;

/** `meta.delivery` van goedkeuren en opnieuw versturen. */
export const reportDeliveryResourceSchema = z.object({
    sent: z.number(),
    failed: z.number(),
    channels: z.array(z.string()),
});

/**
 * `meta.invoice` van goedkeuren en versturen: de factuur die als pdf meeging naar de
 * klant, of `null`. Een antwoord van vóór "Factuur meesturen" heeft het veld niet.
 */
export const reportInvoiceResourceSchema = z.object({
    uuid: z.string(),
    number: z.string(),
    total: z.string(),
    due_at: timestamp,
    filename: z.string(),
    /** Net uit de diensten van de keuring gemaakt. */
    drafted: z.boolean(),
    /** Net genummerd en gedateerd. */
    issued: z.boolean(),
});

export type ReportInvoiceResource = z.infer<typeof reportInvoiceResourceSchema>;

/** `POST /orders/{order}/report` - **202**. */
export const reportGenerationResponseSchema = z.object({
    data: reportResourceSchema,
    meta: z.object({
        job: z.object({
            /** `queued` · `finished` · `failed` */
            status: z.string(),
            report_uuid: z.string(),
        }),
    }),
});

/**
 * `POST /reports/{report}/approve`. De toegangscode staat **alleen hier** in
 * leesbare vorm, en alleen als het bedrijf er een vraagt.
 */
export const reportApprovalResponseSchema = z.object({
    data: reportResourceSchema,
    meta: z.object({
        share: z.object({
            uuid: z.string(),
            url: z.string(),
            valid_until: timestamp,
            access_code: z.string().nullable(),
        }),
        delivery: reportDeliveryResourceSchema,
        invoice: reportInvoiceResourceSchema.nullable().default(null),
    }),
});

/**
 * `POST /reports/{report}/send`. `meta.share` is de link die meeging; `new` als
 * versturen hem moest maken omdat er geen werkende meer was, en alleen dan staat
 * een toegangscode er één keer in leesbare vorm in.
 */
export const reportSendResponseSchema = z.object({
    data: reportResourceSchema,
    meta: z.object({
        share: z.object({
            uuid: z.string(),
            url: z.string(),
            valid_until: timestamp,
            new: z.boolean(),
            access_code: z.string().nullable(),
        }),
        delivery: reportDeliveryResourceSchema,
        invoice: reportInvoiceResourceSchema.nullable().default(null),
    }),
});

/** (`Reporting\ReportShareResource`) - een publieke link zoals kantoor hem beheert. */
export const reportShareResourceSchema = z.object({
    uuid: z.string(),
    report_uuid: z.string(),
    url: z.string().nullable(),
    /** `active` · `expired` · `revoked` */
    state: z.string(),
    valid_until: timestamp,
    show_costs: z.boolean(),
    allow_forward: z.boolean(),
    access_code_required: z.boolean(),
    /** Ook bij een net gemaakte link (`POST`, **201**) een getal: dan `0`. */
    opened_count: z.number(),
    downloaded_count: z.number(),
    forwarded_count: z.number(),
    last_opened_at: timestamp.nullable(),
    revoked_at: timestamp.nullable(),
    created_at: timestamp.nullable(),
});

export type ReportShareResource = z.infer<typeof reportShareResourceSchema>;

/**
 * `GET /orders/{order}/report`: de nieuwste versie in `data`, en in `meta` de
 * versie die de koper heeft - de nieuwste goedgekeurde, met haar nieuwste link
 * die nog werkt. Na "Nieuwe versie maken" is `data` het concept en blijft de
 * verstuurde versie via `meta` bereikbaar.
 */
export const orderReportResponseSchema = z.object({
    data: reportResourceSchema,
    meta: z.object({
        released_report_uuid: z.string().nullable(),
        released_version: z.number().nullable(),
        released_link: reportShareResourceSchema.nullable(),
    }),
});

export type OrderReportResponse = z.infer<typeof orderReportResponseSchema>;

/** `POST` en `PATCH /reports/{report}/shares`: een gezette of gemaakte code staat eenmalig in `meta`. */
export const reportShareResponseSchema = z.object({
    data: reportShareResourceSchema,
    meta: z.object({ access_code: z.string().nullable() }),
});

export const reportSharesResponseSchema = z.object({
    data: z.array(reportShareResourceSchema),
});

/** (`Reporting\ReportStatisticsResource`) - `GET /reports/{report}/statistics`. */
export const reportStatisticsResourceSchema = z.object({
    sent_at: timestamp.nullable(),
    sent_via: z.string().nullable(),
    opened_count: z.number(),
    opened_last_at: timestamp.nullable(),
    downloaded_count: z.number(),
    forwarded_count: z.number(),
    questions_count: z.number(),
    valid_until: timestamp.nullable(),
    url: z.string(),
    short_url: z.string().nullable(),
    costs_visible: z.boolean(),
    sharing_enabled: z.boolean(),
    access_code_required: z.boolean(),
    /** `none` · `active` · `expired` · `revoked` */
    link_state: z.string(),
    links_count: z.number(),
    events: z.array(
        z.object({
            /** `sent` · `opened` · `downloaded` · `forwarded` · `question_asked` */
            type: z.string(),
            label: z.string(),
            channel: z.string().nullable(),
            recipient: z.string().nullable(),
            /** Bij `question_asked` de vraag van de koper, voluit; anders `null`. */
            message: z.string().nullable(),
            occurred_at: timestamp,
        }),
    ),
});

export type ReportStatisticsResource = z.infer<typeof reportStatisticsResourceSchema>;

/** `GET /reports/pdf-service`: wachtende PDF's van het bedrijf, en of de PDF-dienst ontbreekt. */
export const pdfBacklogResourceSchema = z.object({
    waiting: z.number().int().nonnegative(),
    setup_required: z.boolean(),
    last_failure_at: z.string().nullable(),
});

export type PdfBacklogResource = z.infer<typeof pdfBacklogResourceSchema>;
